import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { TutorService } from '../../../../modules/tutor/tutor-onboarding/tutor.service';
import { CommonService } from '../../../../core/common.service';
import { CONSTANTS } from '../../../../core/constants';

export const dateRangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const startYear = control.get('start_year')?.value;
  const startMonth = control.get('start_month')?.value;
  const endYear = control.get('end_year')?.value;
  const endMonth = control.get('end_month')?.value;
  const currentlyWorking = control.get('is_currently_working')?.value;

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  if (!currentlyWorking && startYear && startMonth && endYear && endMonth) {
    const startIdx = months.indexOf(startMonth);
    const endIdx = months.indexOf(endMonth);
    
    if (startIdx !== -1 && endIdx !== -1) {
      const startDate = new Date(startYear, startIdx);
      const endDate = new Date(endYear, endIdx);
      
      if (startDate > endDate) {
        return { dateRange: true };
      }
    }
  }
  return null;
};

@Component({
  selector: 'app-experience-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    DropdownModule,
    MultiSelectModule,
    CheckboxModule,
    TooltipModule
  ],
  templateUrl: './experience-form.component.html',
  styleUrls: ['./experience-form.component.scss']
})
export class ExperienceFormComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() editingItem: any = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<any>();

  constants: any = CONSTANTS;
  tempExperienceForm!: FormGroup;
  experienceDocument: any = null;
  isBtnLoading: boolean = false;
  yearList: any[] = [];
  subjects: any[] = [];

  constructor(
    private _formBuilder: FormBuilder,
    private _tutorService: TutorService,
    private _commonService: CommonService,
    private messageService: MessageService
  ) {
    const startYear = 1990;
    const currentYear = new Date().getFullYear();
    for (let year = startYear; year <= currentYear; year++) {
      this.yearList.push({ name: year.toString(), value: year });
    }
  }

  ngOnInit(): void {
    this.prepareForm();
    this.getSubjectList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editingItem'] && this.tempExperienceForm) {
      this.prepareForm(this.editingItem);
    }
  }

  getSubjectList() {
    this._commonService.getSubjects().subscribe({
      next: (result: any) => {
        if (result && result.success) {
          const rawSubjects = result.data?.subjects || result.data || [];
          this.subjects = rawSubjects.map((s: any) => ({
            ...s,
            id: s.id || s._id
          }));
        }
      }
    });
  }

  prepareForm(data: any = null) {
    this.tempExperienceForm = this._formBuilder.group({
      id: [data?.id || ''],
      type: [data?.type || '', [Validators.required]],
      institution_name: [data?.institution_name || '', [Validators.required]],
      location: [data?.location || '', [Validators.required]],
      subjects: [data?.subjects?.map((s: any) => s.id || s) || data?.subjects || [], [Validators.required, Validators.maxLength(5)]],
      start_month: [data?.start_month || '', [Validators.required]],
      start_year: [data?.start_year || '', [Validators.required]],
      end_month: [data?.end_month || ''],
      end_year: [data?.end_year || ''],
      teaching_summary: [data?.teaching_summary || '', [Validators.required]],
      document: [data?.file?.document || data?.document || ''],
      is_currently_working: [data?.is_currently_working || false],
    }, { validators: dateRangeValidator });

    this.experienceDocument = null;
  }

  closeDialog() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  saveExperienceItem() {
    if (this.tempExperienceForm.invalid) {
      this.tempExperienceForm.markAllAsTouched();
      return;
    }

    this.isBtnLoading = true;
    const formValue = this.tempExperienceForm.value;
    const formData = new FormData();
    formData.append('institution_name', formValue.institution_name);
    formData.append('location', formValue.location);
    formData.append('type', formValue.type);
    formData.append('teaching_summary', formValue.teaching_summary);
    formData.append('is_currently_working', formValue.is_currently_working.toString());
    formData.append('start_month', formValue.start_month.toString());
    formData.append('start_year', formValue.start_year.toString());
    
    if (!formValue.is_currently_working) {
      formData.append('end_month', formValue.end_month.toString());
      formData.append('end_year', formValue.end_year.toString());
    }

    if (formValue.subjects && formValue.subjects.length > 0) {
      formValue.subjects.forEach((id: string) => {
        formData.append('subject_ids', id);
      });
    }

    if (this.experienceDocument) {
      formData.append('file', this.experienceDocument);
    }

    const saveObs$ = formValue.id
      ? this._tutorService.editExperience(formValue.id, formData)
      : this._tutorService.addExperience(formData);

    saveObs$.subscribe({
      next: (res: any) => {
        this.isBtnLoading = false;
        if (res.success) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
          this.saved.emit(res.data);
          this.closeDialog();
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: res.message });
        }
      },
      error: (err: any) => {
        this.isBtnLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error?.message || 'Something went wrong' });
      }
    });
  }

  updateExperienceDocument(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (!(file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png')) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please upload Image in JPEG or PNG format' });
        return;
      }

      this.experienceDocument = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.tempExperienceForm.patchValue({ document: e.target.result });
      };
      reader.readAsDataURL(file);
    }
  }
}
